import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import moment from 'moment';
import { fetchUsers, fetchUserActivities, fetchUserActivityStats } from '../../../slices/userSlice';
import { requestUserActivityExport, fetchExports } from '../../../slices/dataSlice';
import { selectBlurSensitive } from '../../../slices/preferencesSlice';
import { toast } from 'react-toastify';
import Pagination from '../../../components/Pagination';
import { useBreadcrumbs } from '../../../contexts/BreadcrumbContext';
import SearchableSelect from '../../../components/SearchableSelect';
import DateRangePicker from '../../../components/DateRangePicker';
import ActivityCard from './components/ActivityCard';
import CustomButton from '../../../components/CustomButton';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { TableCellsIcon } from '@heroicons/react/24/outline';

const ACTIVITY_TYPES = [
  { value: 'all', label: 'All Activities' },
  { value: 'login', label: 'Logins' },
  { value: 'notification_update', label: 'Notification Updates' },
  { value: 'forecast_submission', label: 'Forecast Submissions' },
  { value: 'forecast_continuous', label: 'Continuous Forecasts' },
  { value: 'file_export', label: 'File Exports' },
  { value: 'profile_update', label: 'Profile Updates' },
];

// Tone tokens reuse the project's brand teal (#05788a) and accent orange.
// Identity lives in the count number (tinted at rest); labels stay neutral
// so these read as eyebrows used across StatsCards / Analytics (uppercase
// tracking-wider on gray-500/400). When a card is selected, its tone fills
// the background and tints both the eyebrow and the number.
const TONE = {
  brand: {
    sub: 'text-brand-600 dark:text-brand-400',
    activeBg: 'bg-brand-50 dark:bg-brand-900/30',
    activeText: 'text-brand-700 dark:text-brand-300',
  },
  accent: {
    sub: 'text-accent-600 dark:text-accent-400',
    activeBg: 'bg-accent-50 dark:bg-accent-900/30',
    activeText: 'text-accent-700 dark:text-accent-300',
  },
};

// Shared label typography (color applied separately so active state can override).
const LABEL_CLASS = 'text-[10px] uppercase tracking-wider font-semibold';
const LABEL_RESTING_COLOR = 'text-gray-500 dark:text-gray-400';

/**
 * Compact KPI card: uppercase eyebrow + tone-tinted tabular number.
 * `emphasis="high"` bumps the value size to highlight the lead metric.
 * `onClick` makes the whole card a filter shortcut; `active` swaps it
 * into the tone's filled state (bg + eyebrow + number all tinted).
 */
function StatCard({ label, value, tone = 'brand', emphasis = 'normal', onClick, active = false }) {
  const t = TONE[tone] || TONE.brand;
  const valueSize = emphasis === 'high'
    ? 'text-2xl font-bold'
    : 'text-xl font-semibold';
  const interactive = onClick
    ? `cursor-pointer transition-colors ${active
        ? t.activeBg
        : 'hover:bg-gray-100/60 dark:hover:bg-gray-700/40'}`
    : '';
  const labelColor = active ? t.activeText : LABEL_RESTING_COLOR;
  const valueColor = active ? t.activeText : t.sub;
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      aria-pressed={onClick ? active : undefined}
      className={`rounded-lg border border-gray-200/70 dark:border-gray-600/70 px-3 py-2 ${interactive} text-left w-full`}
    >
      <span className={`block ${LABEL_CLASS} ${labelColor}`}>{label}</span>
      <div className={`mt-0.5 font-mono tabular-nums ${valueColor} ${valueSize}`}>
        {value}
      </div>
    </Tag>
  );
}

/**
 * Compact metric card with an optional sub-breakdown (per-variable,
 * per-preference, per-export-type). The sub-list inherits the same tone so
 * the breakdown reads as "more detail on the same metric". The card header
 * is a filter shortcut when `onClick` is supplied.
 */
function BreakdownCard({
  label,
  value,
  tone = 'brand',
  emphasis = 'normal',
  breakdown = [],
  itemKey,
  onClick,
  active = false,
  // Drill-down on a sub-item (variable / preference / export_type). When set,
  // each row in the breakdown list becomes a button that toggles the filter.
  // `activeItem` highlights the row that matches the current metadata filter.
  onItemClick,
  activeItem,
}) {
  const t = TONE[tone] || TONE.brand;
  const valueSize = emphasis === 'high'
    ? 'text-2xl font-bold'
    : 'text-xl font-semibold';
  // Card sits transparent on the panel at rest; tone-fills when selected.
  const containerBg = active ? t.activeBg : '';
  const headerLabelColor = active ? t.activeText : LABEL_RESTING_COLOR;
  const headerValueColor = active ? t.activeText : t.sub;
  return (
    <div className={`rounded-lg border border-gray-200/70 dark:border-gray-600/70 ${containerBg} px-3 py-2 transition-colors`}>
      {/* Header row — clickable when a filter mapping exists. */}
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          aria-pressed={active}
          className={`flex w-full items-center justify-between gap-2 cursor-pointer rounded -mx-1 px-1 py-0.5 transition-colors ${
            active ? '' : 'hover:bg-gray-100/60 dark:hover:bg-gray-700/40'
          }`}
        >
          <span className={`${LABEL_CLASS} ${headerLabelColor} truncate`}>{label}</span>
          <span className={`font-mono tabular-nums ${headerValueColor} ${valueSize}`}>
            {value}
          </span>
        </button>
      ) : (
        <div className="flex items-center justify-between gap-2">
          <span className={`${LABEL_CLASS} ${headerLabelColor} truncate`}>{label}</span>
          <span className={`font-mono tabular-nums ${headerValueColor} ${valueSize}`}>
            {value}
          </span>
        </div>
      )}
      {breakdown.length > 0 && (
        <ul className="mt-1.5 space-y-0.5 border-t border-gray-200/70 dark:border-gray-700/60 pt-1.5">
          {breakdown.map((item, idx) => {
            const itemValue = item[itemKey];
            const isItemActive = activeItem === itemValue;
            const rowClass = onItemClick
              ? `flex w-full items-center justify-between text-[11px] rounded -mx-1 px-1 py-0.5 cursor-pointer transition-colors ${
                  isItemActive
                    ? t.activeBg
                    : 'hover:bg-gray-100/60 dark:hover:bg-gray-700/40'
                }`
              : 'flex items-center justify-between text-[11px]';
            const labelClass = `truncate mr-2 ${
              isItemActive
                ? `${t.activeText} font-medium`
                : 'text-gray-500 dark:text-gray-400'
            }`;
            const countClass = `font-mono tabular-nums ${isItemActive ? `${t.activeText} font-semibold` : t.sub}`;
            return (
              <li key={idx}>
                {onItemClick ? (
                  <button
                    type="button"
                    onClick={() => onItemClick(itemValue)}
                    aria-pressed={isItemActive}
                    className={rowClass}
                  >
                    <span className={labelClass}>{itemValue}</span>
                    <span className={countClass}>{item.count}</span>
                  </button>
                ) : (
                  <div className={rowClass}>
                    <span className={labelClass}>{itemValue}</span>
                    <span className={countClass}>{item.count}</span>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default function UserActivity() {
  useBreadcrumbs([{ label: 'Users' }, { label: 'User Activity' }], 'Audit log of user actions and system events');
  const dispatch = useDispatch();
  const usersList = useSelector((state) => state.user?.usersList || []);
  // When "Hide sensitive information in the UI" is on, swap any user-identifying
  // labels (filter options, scope chip) to the username alias instead of the
  // email/full name. Mirrors the pattern used in sessions/index.js and
  // AnalyticsFiltersBar.js — swap the label rather than relying on CSS blur,
  // so the PII never reaches the rendered DOM.
  const blurSensitive = useSelector(selectBlurSensitive);
  const [searchParams, setSearchParams] = useSearchParams();

  // Helper to get saved filters from sessionStorage
  const getSavedFilters = () => {
    try {
      const saved = sessionStorage.getItem('userActivityFilters');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  };

  // Initialize state from URL params, sessionStorage, or defaults
  const today = moment().format('YYYY-MM-DD');
  const savedFilters = getSavedFilters();

  const [selectedUserId, setSelectedUserId] = useState(
    searchParams.get('user') || savedFilters.user || ''
  );
  const [activityType, setActivityType] = useState(
    searchParams.get('type') || savedFilters.type || 'all'
  );
  const [startDate, setStartDate] = useState(
    searchParams.get('start') || savedFilters.start || today
  );
  const [endDate, setEndDate] = useState(
    searchParams.get('end') || savedFilters.end || today
  );
  const [sortBy, setSortBy] = useState(
    searchParams.get('sort') || savedFilters.sort || 'desc'
  );

  // Metadata sub-filter: drills into a specific variable / preference /
  // export_type within a category. Persists alongside the main filters.
  // Shape: { key: 'variable'|'preference'|'export_type', value: string } | null.
  const [metadataFilter, setMetadataFilter] = useState(() => {
    const key = searchParams.get('metaKey') || savedFilters.metaKey;
    const value = searchParams.get('metaValue') || savedFilters.metaValue;
    return key && value ? { key, value } : null;
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(
    parseInt(searchParams.get('page') || savedFilters.page || '1')
  );
  const [pageSize, setPageSize] = useState(
    parseInt(searchParams.get('pageSize') || savedFilters.pageSize || '7')
  );

  // Data state
  const [activities, setActivities] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showExportConfirm, setShowExportConfirm] = useState(false);

  // Update URL params and sessionStorage when filters change
  useEffect(() => {
    const params = {};
    if (selectedUserId) params.user = selectedUserId;
    if (activityType !== 'all') params.type = activityType;
    if (startDate) params.start = startDate;
    if (endDate) params.end = endDate;
    if (sortBy !== 'desc') params.sort = sortBy;
    if (currentPage !== 1) params.page = currentPage.toString();
    if (pageSize !== 7) params.pageSize = pageSize.toString();
    if (metadataFilter?.key && metadataFilter?.value) {
      params.metaKey = metadataFilter.key;
      params.metaValue = metadataFilter.value;
    }

    // Save to URL
    setSearchParams(params, { replace: true });

    // Save to sessionStorage (survives route navigation)
    try {
      sessionStorage.setItem('userActivityFilters', JSON.stringify({
        user: selectedUserId,
        type: activityType,
        start: startDate,
        end: endDate,
        sort: sortBy,
        page: currentPage.toString(),
        pageSize: pageSize.toString(),
        metaKey: metadataFilter?.key || '',
        metaValue: metadataFilter?.value || '',
      }));
    } catch (e) {
      console.error('Failed to save filters to sessionStorage:', e);
    }
  }, [selectedUserId, activityType, startDate, endDate, sortBy, currentPage, pageSize, metadataFilter, setSearchParams]);

  // Fetch users list on mount
  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  // Fetch activities
  const fetchActivities = async () => {
    setLoading(true);
    try {
      const result = await dispatch(fetchUserActivities({
        userId: selectedUserId,
        activityType,
        startDate,
        endDate,
        page: currentPage,
        pageSize,
        ordering: sortBy,
        metadataFilter,
      })).unwrap();
      setActivities(result?.results || []);
      setPagination(result?.pagination || null);
    } catch (error) {
      toast.error('Failed to load activities');
      console.error(error);
      setActivities([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  };

  // Fetch stats. When a specific user is selected, the backend scopes every
  // metric (total, breakdowns) to that user — so the panel reflects exactly
  // what the activity feed below is showing.
  const fetchStats = async () => {
    try {
      const result = await dispatch(fetchUserActivityStats({
        startDate,
        endDate,
        userId: selectedUserId || undefined,
      })).unwrap();
      setStats(result);
    } catch (error) {
      console.error('Failed to load stats', error);
    }
  };

  // Reset to page 1 when filters change (not when changing page/pageSize)
  useEffect(() => {
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserId, activityType, startDate, endDate, sortBy, metadataFilter]);

  // Fetch data when any filter changes
  useEffect(() => {
    // Auto-fetch when any filter changes (including dates)
    // All filters now apply automatically - consistent UX
    if (startDate && endDate) {
      fetchActivities();
      fetchStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserId, activityType, startDate, endDate, currentPage, pageSize, sortBy, metadataFilter]);

  // Don't render until dates are initialized
  if (!startDate || !endDate) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-gray-200 border-t-brand-600 rounded-full animate-spin" />
      </div>
    );
  }

  // Prepare user options for SearchableSelect (sorted alphabetically)
  const userOptions = [
    { value: '', label: 'All Users' },
    ...usersList
      .map(user => ({
        value: user.id,
        label: blurSensitive
          ? (user.username || user.email)
          : (user.first_name && user.last_name
              ? `${user.first_name} ${user.last_name} (${user.email})`
              : user.email)
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  ];

  // Selected user, used to render the scope indicator above the stats panel.
  // null when "All Users" is active (global scope).
  const selectedUser = selectedUserId
    ? usersList.find(u => u.id === selectedUserId)
    : null;
  const selectedUserLabel = selectedUser
    ? (blurSensitive
        ? (selectedUser.username || selectedUser.email)
        : (selectedUser.first_name && selectedUser.last_name
            ? `${selectedUser.first_name} ${selectedUser.last_name}`
            : selectedUser.email))
    : null;

  // Calculate stats for display
  const totalActivities = stats?.total_activities || 0;
  const uniqueUsers = stats?.unique_users || 0;
  const loginStats = stats?.by_type?.find(t => t.activity_type === 'login');
  const logins = loginStats?.count || 0;
  const loginBreakdown = loginStats?.breakdown || [];

  const notificationStats = stats?.by_type?.find(t => t.activity_type === 'notification_update');
  const notificationUpdates = notificationStats?.count || 0;
  const notificationBreakdown = notificationStats?.breakdown || [];

  // Two distinct submission modes — see docs/how_to_forecast.md:
  //  - Session forecasts (forecast_submission): one-per-challenge, posted
  //    before gate closure.
  //  - Continuous forecasts (forecast_continuous): rolling series the
  //    platform slices into per-session submissions automatically.
  // They feed the same scoring pipeline but reflect different operational
  // patterns, so the stats panel keeps them separate.
  const sessionForecastStats = stats?.by_type?.find(t => t.activity_type === 'forecast_submission');
  const sessionForecasts = sessionForecastStats?.count || 0;
  const sessionForecastBreakdown = sessionForecastStats?.breakdown || [];

  const continuousForecastStats = stats?.by_type?.find(t => t.activity_type === 'forecast_continuous');
  const continuousForecasts = continuousForecastStats?.count || 0;
  const continuousForecastBreakdown = continuousForecastStats?.breakdown || [];

  const exportStats = stats?.by_type?.find(t => t.activity_type === 'file_export');
  const fileExports = exportStats?.count || 0;
  const exportBreakdown = exportStats?.breakdown || [];

  const profileUpdates = stats?.by_type?.find(t => t.activity_type === 'profile_update')?.count || 0;

  // Toggle activity-type filter from a stats card click. Clicking the
  // currently active filter clears it back to 'all', so the same gesture
  // both applies and removes the filter. Switching category always drops
  // any sub-filter that belonged to the previous category.
  const toggleActivityFilter = (type) => {
    setActivityType(prev => (prev === type ? 'all' : type));
    setMetadataFilter(null);
  };

  // Drill-down: clicking a sub-item (variable / preference / export_type)
  // selects the matching activity_type AND the metadata sub-filter. Clicking
  // the already-active sub-item clears only the sub-filter (keeps category).
  const toggleMetadataFilter = (type, key, value) => {
    setMetadataFilter(prev => (
      prev && prev.key === key && prev.value === value ? null : { key, value }
    ));
    setActivityType(type);
  };

  // Clear all filters
  const handleClearFilters = () => {
    const today = moment().format('YYYY-MM-DD');
    setSelectedUserId('');
    setActivityType('all');
    setStartDate(today);
    setEndDate(today);
    setSortBy('desc');
    setCurrentPage(1);
    setPageSize(7);
    setMetadataFilter(null);

    // Clear sessionStorage
    try {
      sessionStorage.removeItem('userActivityFilters');
    } catch (e) {
      console.error('Failed to clear filters from sessionStorage:', e);
    }
  };

  // Handle export confirmation
  const handleExportClick = () => {
    setShowExportConfirm(true);
  };

  // Handle actual export request after confirmation
  const handleExportConfirm = async () => {
    setShowExportConfirm(false);
    setExportLoading(true);
    try {
      const payload = {
        start_date: startDate,
        end_date: endDate,
      };

      // Add optional filters
      if (selectedUserId) {
        payload.user_id = selectedUserId;
      }
      if (activityType && activityType !== 'all') {
        payload.activity_type = activityType;
      }

      await dispatch(requestUserActivityExport(payload)).unwrap();

      // Refresh exports list
      dispatch(fetchExports());

      toast.success('Export request submitted successfully. You can download the file from the Files section.');
    } catch (error) {
      const errorMessage = error?.message || error?.error || 'Failed to request export';
      toast.error(errorMessage);
      console.error('Export error:', error);
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Main Layout: Resources-style 3-column grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:items-stretch">

        {/* Left Column: Filters (1/3 width) */}
        <div className="lg:col-span-1 flex">
          <div className="rounded-xl bg-surface dark:bg-gray-870 shadow-sm border border-gray-200/70 dark:border-gray-600/70 w-full divide-y divide-gray-100 dark:divide-gray-700">

            {/* Stats Summary — brand-tonal compact cards. When a single user
                is selected, every metric reflects that user only (backend
                scopes the queryset by user_id). Cards double as filter
                shortcuts: clicking one applies the matching activity_type,
                clicking again clears the filter. */}
            <div className="p-6">
              <div className="flex items-baseline justify-between gap-2 mb-1">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Statistics</h3>
                {selectedUserLabel ? (
                  <span
                    className="inline-flex items-center gap-1 rounded-full bg-brand-50 dark:bg-brand-900/40 px-2 py-0.5 text-[10px] font-medium text-brand-700 dark:text-brand-300 max-w-[60%] truncate"
                    title={`Scoped to: ${selectedUserLabel}`}
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-brand-500" aria-hidden="true" />
                    {selectedUserLabel}
                  </span>
                ) : null}
              </div>
              {/* Sub-header: scope summary. "Active Users" lives here when
                  global — promoting it to a full card creates an awkward
                  blank cell once we scope to one user. */}
              <p className="text-[10px] uppercase tracking-wider font-medium text-gray-400 dark:text-gray-500 mb-3">
                {selectedUserLabel
                  ? 'Scoped to a single user'
                  : <>All users · <span className="text-gray-600 dark:text-gray-300 font-mono tabular-nums">{uniqueUsers}</span> active in range</>
                }
              </p>

              {/* KPI card — Total spans the row as the lead metric. The
                  remaining metrics live in the breakdown stack below so each
                  can carry a sub-list (logins by source, forecasts by
                  variable, etc.). */}
              <div className="grid grid-cols-2 gap-2">
                <div className="col-span-2">
                  <StatCard
                    label="Total"
                    value={totalActivities}
                    tone="brand"
                    emphasis="high"
                    onClick={() => setActivityType('all')}
                    active={activityType === 'all'}
                  />
                </div>
              </div>

              {/* Activity type breakdowns. Each card carries an optional
                  sub-list (source / variable / preference / export_type).
                  Logins decompose into web (dashboard) vs api (programmatic);
                  Session vs. Continuous forecasts are separate submission
                  modes. */}
              <div className="mt-3 space-y-2">
                <BreakdownCard
                  label="Logins"
                  value={logins}
                  tone="brand"
                  breakdown={loginBreakdown}
                  itemKey="source"
                  onClick={() => toggleActivityFilter('login')}
                  active={activityType === 'login'}
                  onItemClick={(v) => toggleMetadataFilter('login', 'source', v)}
                  activeItem={activityType === 'login' && metadataFilter?.key === 'source' ? metadataFilter.value : null}
                />
                <BreakdownCard
                  label="Session Forecasts"
                  value={sessionForecasts}
                  tone="brand"
                  breakdown={sessionForecastBreakdown}
                  itemKey="variable"
                  onClick={() => toggleActivityFilter('forecast_submission')}
                  active={activityType === 'forecast_submission'}
                  onItemClick={(v) => toggleMetadataFilter('forecast_submission', 'variable', v)}
                  activeItem={activityType === 'forecast_submission' && metadataFilter?.key === 'variable' ? metadataFilter.value : null}
                />
                <BreakdownCard
                  label="Continuous Forecasts"
                  value={continuousForecasts}
                  tone="brand"
                  breakdown={continuousForecastBreakdown}
                  itemKey="variable"
                  onClick={() => toggleActivityFilter('forecast_continuous')}
                  active={activityType === 'forecast_continuous'}
                  onItemClick={(v) => toggleMetadataFilter('forecast_continuous', 'variable', v)}
                  activeItem={activityType === 'forecast_continuous' && metadataFilter?.key === 'variable' ? metadataFilter.value : null}
                />
                <BreakdownCard
                  label="Notifications"
                  value={notificationUpdates}
                  tone="brand"
                  breakdown={notificationBreakdown}
                  itemKey="preference"
                  onClick={() => toggleActivityFilter('notification_update')}
                  active={activityType === 'notification_update'}
                  onItemClick={(v) => toggleMetadataFilter('notification_update', 'preference', v)}
                  activeItem={activityType === 'notification_update' && metadataFilter?.key === 'preference' ? metadataFilter.value : null}
                />
                <BreakdownCard
                  label="Exports"
                  value={fileExports}
                  tone="accent"
                  breakdown={exportBreakdown}
                  itemKey="export_type"
                  onClick={() => toggleActivityFilter('file_export')}
                  active={activityType === 'file_export'}
                  onItemClick={(v) => toggleMetadataFilter('file_export', 'export_type', v)}
                  activeItem={activityType === 'file_export' && metadataFilter?.key === 'export_type' ? metadataFilter.value : null}
                />
                <BreakdownCard
                  label="Profile Updates"
                  value={profileUpdates}
                  tone="brand"
                  onClick={() => toggleActivityFilter('profile_update')}
                  active={activityType === 'profile_update'}
                />
              </div>
            </div>

            {/* Filters Section */}
            <div className="p-6">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Filters</h3>
              <div className="space-y-4">

                {/* User Filter */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                    User
                  </label>
                  <SearchableSelect
                    value={selectedUserId}
                    onChange={setSelectedUserId}
                    options={userOptions}
                    placeholder="Select user..."
                    showCount={false}
                  />
                </div>

                {/* Activity Type Filter */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Activity Type
                  </label>
                  <SearchableSelect
                    value={activityType}
                    onChange={setActivityType}
                    options={ACTIVITY_TYPES}
                    placeholder="Select type..."
                    showCount={false}
                  />
                </div>

                {/* Date Range */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Date Range
                  </label>
                  <DateRangePicker
                    startDate={startDate}
                    endDate={endDate}
                    onDateRangeChange={(newStart, newEnd) => {
                      setStartDate(newStart);
                      setEndDate(newEnd);
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Actions Section */}
            <div className="p-6">
              <div className="flex items-center gap-4">
                <CustomButton
                  type="button"
                  icon="table"
                  caption={exportLoading ? 'Exporting...' : 'Export to Excel'}
                  onClick={handleExportClick}
                  disabled={exportLoading}
                  size="mediumSend"
                  className={exportLoading ? 'opacity-60 cursor-wait' : ''}
                />
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 text-sm text-gray-900 dark:text-gray-100"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Export Confirmation Modal */}
        <Dialog
          open={showExportConfirm}
          onClose={() => setShowExportConfirm(false)}
          className="relative z-50"
        >
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/30 dark:bg-black/60" aria-hidden="true" />

          {/* Centered modal */}
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <DialogPanel className="w-full max-w-md transform overflow-hidden rounded-xl bg-surface p-6 text-left align-middle shadow-2xl transition-all border border-gray-200/70 dark:bg-gray-870 dark:border-gray-600/70 dark:text-gray-100">
              <div className="flex items-start">
                {/* Icon */}
                <div className="flex-shrink-0 mt-1 mr-4">
                  <div className="flex items-center justify-center h-10 w-10 rounded-full bg-brand-100 dark:bg-brand-900">
                    <TableCellsIcon
                      className="h-6 w-6 text-brand-600 dark:text-brand-300"
                      aria-hidden="true"
                    />
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1">
                  <DialogTitle
                    as="h3"
                    className="text-lg font-medium text-gray-900 dark:text-gray-100 leading-6"
                  >
                    Export User Activity
                  </DialogTitle>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 dark:text-gray-300">
                      Export user activity data from <strong>{moment(startDate).format('MMM D, YYYY')}</strong> to <strong>{moment(endDate).format('MMM D, YYYY')}</strong>?
                    </p>
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-300">
                      The exported Excel file will be available in the Files section once processing is complete.
                    </p>
                  </div>
                </div>
              </div>

              {/* Action buttons */}
              <div className="mt-4 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowExportConfirm(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 dark:text-gray-200 dark:hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleExportConfirm}
                  className="px-4 py-2 text-sm font-medium border border-brand-500 bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 hover:bg-brand-100 dark:hover:bg-brand-900/50 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                >
                  Export
                </button>
              </div>
            </DialogPanel>
          </div>
        </Dialog>

        {/* Right Column: Activity Feed (2/3 width) */}
        <div className="lg:col-span-2 flex">
          <div className="rounded-xl bg-surface dark:bg-gray-870 shadow-sm border border-gray-200/70 dark:border-gray-600/70 w-full flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-gray-100 dark:border-gray-700">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">User Activity Logs</h2>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Browse user activities across the platform.</p>
                </div>

                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">Sort:</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="block rounded-md border border-gray-300 dark:border-gray-600 bg-surface dark:bg-gray-870 px-2 py-1.5 text-xs text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 cursor-pointer"
                  >
                    <option value="desc">Newest First</option>
                    <option value="asc">Oldest First</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Activity List */}
            <div className="p-6 flex-1">
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="w-8 h-8 border-4 border-gray-200 border-t-brand-600 rounded-full animate-spin" />
                </div>
              ) : activities.length > 0 ? (
                <div>
                  {activities.map((activity) => (
                    <ActivityCard key={activity.id} activity={activity} />
                  ))}
                </div>
              ) : (
                <div className="flex justify-center items-center py-12">
                  <p className="text-gray-500 dark:text-gray-400">No activities found</p>
                </div>
              )}
            </div>

            {/* Pagination - Always at bottom */}
            {pagination && (
              <div className="p-6 pt-0">
                <Pagination
                  currentPage={pagination.current_page}
                  totalPages={pagination.total_pages}
                  totalItems={pagination.total_items}
                  itemsPerPage={pageSize}
                  onPageChange={setCurrentPage}
                  onItemsPerPageChange={setPageSize}
                  itemsPerPageOptions={[7, 10, 20, 50, 100]}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
